import { Fragment, ReactNode, useState } from "react";
import { useCheckForNewDocuments } from "api/documents/useGetDocumentCheck";
import { Portfolio, Representee, useGetContactInfo } from "api/initial/useGetContactInfo";
import classNames from "classnames";
import { Logo } from "components/Logo/Logo";
import { PortfolioSelect } from "components/PortfolioSelect/PortfolioSelect";
import { useMatchesBreakpoint } from "hooks/useMatchesBreakpoint";
import { useModifiedTranslation } from "hooks/useModifiedTranslation";
import { useGetContractIdData, SelectedContact } from "providers/ContractIdProvider";
import { useKeycloak } from "providers/KeycloakProvider";
import { NavLink, useNavigate } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { initials } from "utils/initials";
import { UserMenu } from "./UserMenu";

export const Navbar = () => {
  const isMd = useMatchesBreakpoint("lg");

  const { linkedContact, userProfile } = useKeycloak();
  const isCustomer = userProfile?.username?.toLowerCase()?.startsWith("f-") ?? false;

  const { data } = useGetContactInfo(false, linkedContact, true, !isCustomer);
  const { selectedContactId, setSelectedContactId, setSelectedContact } = useGetContractIdData();
  const navigate = useNavigate();

  const representees = data?.representees?.filter((x) => x.contactId.startsWith("F")) ?? [];
  const ownContact: SelectedContact = {
    id: data?.contactId,
    contactId: data?._contactId,
    userName: data?.name,
  };

  const handleContactSwitch = (contact: SelectedContact) => {
    setSelectedContactId(contact.id);
    setSelectedContact(contact);
    navigate("/overview");
  };

  return (
    <nav className="fixed top-0 left-0 margin h-[var(--navbar-height)] w-full bg-capBeige z-[1000]">
      <div id="capguard-navbar" className="w-full h-full flex flex-col justify-start">
        <div className="w-full h-full flex items-start pt-8">
          <div className="w-full flex-1 h-auto lg:h-12 flex justify-between items-center">
            <div className="flex justify-start items-center gap-6">
              <Logo />
              <PortfolioSelect />
            </div>
            <div className="flex flex-1 justify-end items-center gap-4">
              {isCustomer && representees.length > 0 && (
                <RepresenteeSwitcher
                  ownContact={ownContact}
                  representees={representees}
                  selectedContactId={selectedContactId}
                  onChange={handleContactSwitch}
                />
              )}
              <UserMenu />
            </div>
          </div>
        </div>
        <div className="w-full flex justify-start items-center border-b border-capGreen bg-capBeige gap-4 py-2 lg:py-4 lg:pb-8 overflow-x-scroll overflow-y-hidden no-scrollbar">
          {isMd && <NavItems />}
        </div>
      </div>
    </nav>
  );
};

export const isUserProfessional = (classification: string | undefined) => {
  return classification === "PROF";
};

export const hasEkb = (portfolios: Portfolio[]) => {
  return portfolios.some((pf) => pf.modelPortfolio?.name.includes("CAPGUARDEKB"));
};
const userIsAllowed = (username: string | undefined) => {
  return username?.toLocaleLowerCase().startsWith("inv") || false;
};

interface NavItemsProps {
  onClick?: () => void;
}
export const NavItems = ({ onClick }: NavItemsProps) => {
  const { t } = useModifiedTranslation();
  const { userProfile } = useKeycloak();
  const userIsInternal = userIsAllowed(userProfile?.username);
  const { selectedContactId } = useGetContractIdData();
  const { data, loading } = useGetContactInfo(false, selectedContactId);

  const userIsProfessional = isUserProfessional(data?.classification?.code);
  const userHasEkb = data && hasEkb(data.portfolios);

  const { data: latestNewDocumentTime } = useCheckForNewDocuments();

  const [dismissedAt, setDismissedAt] = useState<number>(() =>
    Number(sessionStorage.getItem("documents-dismissed-at") ?? 0)
  );

  const showDocumentsHighlight =
    latestNewDocumentTime !== false && latestNewDocumentTime > dismissedAt;

  const handleDocumentsClick = () => {
    if (showDocumentsHighlight) {
      const now = Date.now();
      sessionStorage.setItem("documents-dismissed-at", String(now));
      setDismissedAt(now);
    }
    onClick?.();
  };

  return (
    <>
      <NavElement onClick={onClick} to="overview">
        {t("navTab.tabs.overview")}
      </NavElement>
      <NavElement onClick={onClick} to="holdings">
        {t("navTab.tabs.holdings")}
      </NavElement>
      <NavElement onClick={onClick} to="transactions">
        {t("navTab.tabs.transactions")}
      </NavElement>
      <NavElement onClick={onClick} to="orders">
        {t("navTab.tabs.orders")}
      </NavElement>
      <NavElement
        onClick={handleDocumentsClick}
        to="documents"
        highlight={showDocumentsHighlight}
        tooltipContent={showDocumentsHighlight ? t("navTab.tabs.documentsTooltip") : undefined}
      >
        {t("navTab.tabs.documents")}
      </NavElement>
      {!userIsProfessional && (
        <NavElement onClick={onClick} to="profile">
          {t("navTab.tabs.profile")}
        </NavElement>
      )}

      <NavElement onClick={onClick} to="contact">
        {t("navTab.tabs.contact")}
      </NavElement>
      {userIsInternal && (
        <NavElement onClick={onClick} to="analysis">
          {t("navTab.tabs.analysis")}
        </NavElement>
      )}
      {userHasEkb && (
        <NavElement onClick={onClick} to="ekb">
          {t("navTab.tabs.ekb")}
        </NavElement>
      )}
    </>
  );
};

interface NavElementProps {
  to: string;
  children: ReactNode;
  onClick?: () => void;
  highlight?: boolean;
  tooltipContent?: string;
}
const NavElement = ({ to, children, onClick, highlight, tooltipContent }: NavElementProps) => {
  const tooltipId = `nav-highlight-${to}`;
  return (
    <NavLink
      to={to}
      onClick={onClick}
      data-tooltip-id={highlight && tooltipContent ? tooltipId : undefined}
      className={({ isActive }) =>
        classNames("cursor-pointer whitespace-nowrap relative", {
          "!font-extrabold underline": isActive,
        })
      }
    >
      {children}
      {highlight && (
        <>
          <span className="absolute -top-0.5 -right-1.5 w-2 h-2 rounded-full bg-capLightGreen" />
          {tooltipContent && (
            <Tooltip
              id={tooltipId}
              content={tooltipContent}
              place="bottom"
              positionStrategy="fixed"
            />
          )}
        </>
      )}
    </NavLink>
  );
};

const AVATAR_COLORS = [
  "#0A680F",
  "#3130ff",
  "#9F059F",
  "#003000",
  "#B60203",
  "#934100",
  "#5D5C00",
  "#00636B",
  "#660033",
  "#000066",
];

interface RepresenteeSwitcherProps {
  ownContact: SelectedContact;
  representees: Representee[];
  selectedContactId: string | number | undefined;
  onChange: (contact: SelectedContact) => void;
}

const RepresenteeSwitcher = ({
  ownContact,
  representees,
  selectedContactId,
  onChange,
}: RepresenteeSwitcherProps) => {
  const isMd = useMatchesBreakpoint("md");

  const allContacts: SelectedContact[] = [
    ownContact,
    ...representees.map((r) => ({ id: r.id, contactId: r.contactId, userName: r.name })),
  ].slice(0, isMd ? 6 : 2); // limit to 10 contacts to avoid overflow in navbar

  return (
    <div className="flex flex-col items-start gap-0.5 md:pr-2">
      <span className="hidden md:block text-[11px] text-capGreen/60 font-semibold uppercase tracking-wide leading-none">
        Mine engasjement
      </span>
      <div className="flex items-center gap-2 pt-1.5">
        {allContacts.map((contact, index) => {
          const isSelected = String(contact.id) === String(selectedContactId);
          const color = AVATAR_COLORS[index % AVATAR_COLORS.length];
          const tooltipId = `rep-switcher-${contact.id}`;
          return (
            <Fragment key={contact.id}>
              <button
                id={tooltipId}
                onClick={() => onChange(contact)}
                style={{ backgroundColor: color }}
                className={classNames(
                  "w-7 h-7 md:w-8 md:h-8 leading-none rounded-full text-xs font-semibold text-white flex items-center justify-center transition-all shrink-0",
                  {
                    "ring-2 ring-offset-1 ring-capGreen scale-110": isSelected,
                    "opacity-60 hover:opacity-100": !isSelected,
                  }
                )}
              >
                {initials(contact.userName ?? "")}
              </button>
              <Tooltip
                anchorSelect={`#${tooltipId}`}
                content={contact.userName}
                place="bottom"
                positionStrategy="fixed"
              />
            </Fragment>
          );
        })}
      </div>
    </div>
  );
};
